import type {
  FlowchartEdge,
  FlowchartNode,
  FlowchartNodeStyle,
  FlowchartSubgraph,
} from './flowchart-parser-model';
import { mergeClassNames, type FlowchartParserState } from './flowchart-parser-state';

interface FlowchartStatementHandlerInput {
  line: string;
  state: FlowchartParserState;
}

interface ClassTargetInput {
  id: string;
  classNames: readonly string[];
  nodesById: Map<string, FlowchartNode>;
  subgraphs: FlowchartSubgraph[];
  edges: FlowchartEdge[];
  pendingClassNamesById: Map<string, string[]>;
}

interface StyleTargetInput {
  id: string;
  style: FlowchartNodeStyle;
  nodesById: Map<string, FlowchartNode>;
  subgraphs: FlowchartSubgraph[];
  pendingStylesById: Map<string, FlowchartNodeStyle>;
}

interface StyleCommaScanState {
  parts: string[];
  buffer: string;
  escaped: boolean;
}

interface StyleCommaCharInput {
  state: StyleCommaScanState;
  char: string;
}

type ClassTargetApplier = (input: ClassTargetInput) => boolean;
type StyleTargetApplier = (input: StyleTargetInput) => boolean;
type StyleCommaCharHandler = (input: StyleCommaCharInput) => StyleCommaScanState | null;

const CLASSDEF_REGEX = /^classDef\s+([A-Za-z0-9_,\s-]+?)\s+(.+)$/i;
const CLASS_STMT_REGEX = /^class\s+([A-Za-z0-9_,\s-]+?)\s+([A-Za-z0-9_-]+)$/i;
const STYLE_STMT_REGEX = /^style\s+([A-Za-z0-9_-]+)\s+(.+)$/i;

const STYLE_KEY_MAP: Record<string, keyof FlowchartNodeStyle> = {
  fill: 'fill',
  stroke: 'stroke',
  color: 'color',
  'stroke-width': 'strokeWidth',
  strokeWidth: 'strokeWidth',
};

const INITIAL_STYLE_COMMA_SCAN_STATE: StyleCommaScanState = {
  parts: [],
  buffer: '',
  escaped: false,
};

function appendEscapedStyleChar(input: StyleCommaCharInput): StyleCommaScanState | null {
  if (!input.state.escaped) {
    return null;
  }
  return {
    parts: input.state.parts,
    buffer: `${input.state.buffer}${input.char}`,
    escaped: false,
  };
}

function startStyleEscape(input: StyleCommaCharInput): StyleCommaScanState | null {
  if (input.char !== '\\') {
    return null;
  }
  return {
    parts: input.state.parts,
    buffer: `${input.state.buffer}${input.char}`,
    escaped: true,
  };
}

function splitStyleComma(input: StyleCommaCharInput): StyleCommaScanState | null {
  if (input.char !== ',') {
    return null;
  }
  return {
    parts: [...input.state.parts, input.state.buffer],
    buffer: '',
    escaped: false,
  };
}

function appendStyleChar(input: StyleCommaCharInput): StyleCommaScanState {
  return {
    parts: input.state.parts,
    buffer: `${input.state.buffer}${input.char}`,
    escaped: false,
  };
}

const STYLE_COMMA_CHAR_HANDLERS: ReadonlyArray<StyleCommaCharHandler> = [
  appendEscapedStyleChar,
  startStyleEscape,
  splitStyleComma,
];

function scanStyleCommaChar(input: StyleCommaCharInput): StyleCommaScanState {
  return (
    STYLE_COMMA_CHAR_HANDLERS.reduce<StyleCommaScanState | null>(
      (matched, handler) => matched || handler(input),
      null,
    ) ?? appendStyleChar(input)
  );
}

function splitTopLevelCommas(raw: string): string[] {
  const finalState = Array.from(raw).reduce<StyleCommaScanState>(
    (state, char) => scanStyleCommaChar({ state, char }),
    INITIAL_STYLE_COMMA_SCAN_STATE,
  );
  return [...finalState.parts, finalState.buffer];
}

function parseStyleBody(raw: string): FlowchartNodeStyle {
  return splitTopLevelCommas(raw.replace(/;\s*$/, '')).reduce<FlowchartNodeStyle>((style, pair) => {
    const separatorIndex = pair.indexOf(':');
    if (separatorIndex < 0) {
      return style;
    }
    const rawKey = pair.slice(0, separatorIndex).trim();
    const value = pair.slice(separatorIndex + 1).trim().replace(/\\,/g, ',');
    const key = STYLE_KEY_MAP[rawKey];
    if (!key || !value) {
      return style;
    }
    return { ...style, [key]: value };
  }, {});
}

function readCommaSeparatedTokens(raw: string): string[] {
  return raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function commitClassDef(input: {
  match: RegExpMatchArray;
  classDefs: Map<string, FlowchartNodeStyle>;
}): void {
  const classNames = readCommaSeparatedTokens(input.match[1] ?? '');
  const style = parseStyleBody(input.match[2] ?? '');
  classNames.forEach((className) => input.classDefs.set(className, style));
}

function applyClassNamesToNode(input: ClassTargetInput): boolean {
  const node = input.nodesById.get(input.id);
  if (!node) {
    return false;
  }
  input.nodesById.set(input.id, {
    ...node,
    classNames: mergeClassNames(node.classNames, input.classNames),
  });
  return true;
}

function applyClassNamesToSubgraph(input: ClassTargetInput): boolean {
  const subgraphIndex = input.subgraphs.findIndex((subgraph) => subgraph.id === input.id);
  const subgraph = input.subgraphs[subgraphIndex];
  if (!subgraph) {
    return false;
  }
  input.subgraphs[subgraphIndex] = {
    ...subgraph,
    classNames: mergeClassNames(subgraph.classNames, input.classNames),
  };
  return true;
}

function applyClassNamesToEdge(input: ClassTargetInput): boolean {
  const edgeIndex = input.edges.findIndex(
    (edge) => edge.edgeId === input.id || edge.id === input.id,
  );
  const edge = input.edges[edgeIndex];
  if (!edge) {
    return false;
  }
  input.edges[edgeIndex] = {
    ...edge,
    classNames: mergeClassNames(edge.classNames, input.classNames),
  };
  return true;
}

function rememberPendingClassNames(input: ClassTargetInput): void {
  const pending = input.pendingClassNamesById.get(input.id) ?? [];
  input.pendingClassNamesById.set(input.id, mergeClassNames(pending, input.classNames));
}

const CLASS_TARGET_APPLIERS: ReadonlyArray<ClassTargetApplier> = [
  applyClassNamesToNode,
  applyClassNamesToSubgraph,
  applyClassNamesToEdge,
];

function applyClassNamesToTarget(input: ClassTargetInput): void {
  const applied = CLASS_TARGET_APPLIERS.some((applier) => applier(input));
  if (applied) {
    return;
  }
  rememberPendingClassNames(input);
}

function applyClassStatement(input: {
  match: RegExpMatchArray;
  state: FlowchartParserState;
}): void {
  const className = input.match[2] ?? '';
  const targetIds = readCommaSeparatedTokens(input.match[1] ?? '');
  targetIds.forEach((targetId) =>
    applyClassNamesToTarget({
      id: targetId,
      classNames: [className],
      nodesById: input.state.nodesById,
      subgraphs: input.state.subgraphs,
      edges: input.state.edges,
      pendingClassNamesById: input.state.pendingClassNamesById,
    }),
  );
}

function applyStyleToNode(input: StyleTargetInput): boolean {
  const node = input.nodesById.get(input.id);
  if (!node) {
    return false;
  }
  input.nodesById.set(input.id, { ...node, inlineStyle: input.style });
  return true;
}

function applyStyleToSubgraph(input: StyleTargetInput): boolean {
  const subgraphIndex = input.subgraphs.findIndex((subgraph) => subgraph.id === input.id);
  const subgraph = input.subgraphs[subgraphIndex];
  if (!subgraph) {
    return false;
  }
  input.subgraphs[subgraphIndex] = { ...subgraph, inlineStyle: input.style };
  return true;
}

function rememberPendingStyle(input: StyleTargetInput): void {
  input.pendingStylesById.set(input.id, input.style);
}

const STYLE_TARGET_APPLIERS: ReadonlyArray<StyleTargetApplier> = [
  applyStyleToNode,
  applyStyleToSubgraph,
];

function applyInlineStyleToTarget(input: StyleTargetInput): void {
  const applied = STYLE_TARGET_APPLIERS.some((applier) => applier(input));
  if (applied) {
    return;
  }
  rememberPendingStyle(input);
}

function applyStyleStatement(input: {
  match: RegExpMatchArray;
  state: FlowchartParserState;
}): void {
  const id = input.match[1] ?? '';
  const style = parseStyleBody(input.match[2] ?? '');
  applyInlineStyleToTarget({
    id,
    style,
    nodesById: input.state.nodesById,
    subgraphs: input.state.subgraphs,
    pendingStylesById: input.state.pendingStylesById,
  });
}

export function handleClassDefLine(input: FlowchartStatementHandlerInput): boolean {
  const classDefMatch = input.line.match(CLASSDEF_REGEX);
  if (!classDefMatch) {
    return false;
  }
  commitClassDef({ match: classDefMatch, classDefs: input.state.classDefs });
  return true;
}

export function handleClassStatementLine(input: FlowchartStatementHandlerInput): boolean {
  const classStmtMatch = input.line.match(CLASS_STMT_REGEX);
  if (!classStmtMatch) {
    return false;
  }
  applyClassStatement({ match: classStmtMatch, state: input.state });
  return true;
}

export function handleStyleStatementLine(input: FlowchartStatementHandlerInput): boolean {
  const styleStmtMatch = input.line.match(STYLE_STMT_REGEX);
  if (!styleStmtMatch) {
    return false;
  }
  applyStyleStatement({ match: styleStmtMatch, state: input.state });
  return true;
}
